import { useCallback, useState } from "react";
import { getApiUrl } from "src/environment";
import { getApiAuthData, getDateTimeRecord, redirectToLogin } from "src/utils";
import { Notification } from "src/utils/Notification";

import { IPostRequest } from "../interfaces/Post";
import { useDispatch } from "../redux/hooks";
import { addNewEntryRequest } from "../redux/slices/generatorFeaturesSlice";
import { parseErrorMessage } from "../utils";
import { useIsNewEntry } from "./useIsNewEntry";

const SILENT_POST_REQUESTS = new Set([
  "logout",
  "release-entry-lock",
  "get-entry-lock",
  "check-entry-lock",
  "keep-alive-entry-lock",
]);

const isSilentPostRequest = (request: string) =>
  SILENT_POST_REQUESTS.has(request);

export interface IPostProps extends IPostRequest {
  shouldRunAfterAutosave?: boolean;
}
/**
 * Custom hook to handle API POST requests.
 *
 * @returns An object containing the `post` function, a loading state, and an error state.
 */
export const usePostApi = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const isNewEntry = useIsNewEntry();
  /**
   * Sends a POST request to the API.
   *
   * @param request - The API request endpoint.
   * @param body - The request body to send with the API call.
   * @param onSuccess - (Optional) A callback function to execute on a successful response.
   * @param onError - (Optional) A callback function to execute on an error response.
   * @param shouldRunAfterAutosave - (Optional) A boolean indicating whether the request should run after autosave. Defaults to false.
   * @returns A promise that resolves to the API response or undefined.
   */
  const post = useCallback(
    async ({
      request,
      body,
      onSuccess,
      onError,
      shouldRunAfterAutosave,
    }: IPostProps) => {
      setIsLoading(true);
      let errorRes = null;
      try {
        let result: any = null;
        if (window?.api?.post) {
          result = await window.api.post(request, body);
        } else {
          if (shouldRunAfterAutosave && isNewEntry) {
            dispatch(
              addNewEntryRequest({
                request,
                body,
                onSuccess,
                onError,
              }),
            );
            setIsLoading(false);
            return;
          }
          const response = await fetch(getApiUrl(), {
            method: "POST",
            body: JSON.stringify({
              request,
              _app: true,
              ...getApiAuthData(),
              ...body,
              dto: getDateTimeRecord(),
            }),
          });
          if (!response.ok) {
            throw new Error(`Server Error Occured! Code:${response.status}`);
          }
          result = await response.json();
        }

        if (result?.logout && !isSilentPostRequest(request)) {
          redirectToLogin();
          throw new Error("error in post");
        }

        if (result?.error && !isSilentPostRequest(request)) {
          errorRes = result;
          throw new Error(
            typeof result.error === "string"
              ? result.error
              : "Something went wrong.",
          );
        }
        if (result?.success && result?.message && !result?.warnUser) {
          Notification("Offline", result.message, "green");
        }

        if (onSuccess) onSuccess(result);

        return result;
      } catch (error) {
        setIsError(true);
        onError?.(errorRes);
        if (
          !isSilentPostRequest(request) &&
          !String(error).includes("You're using an outdated")
        )
          Notification("Error", parseErrorMessage(String(error)), "red");
      } finally {
        setIsLoading(false);
      }
    },
    [isNewEntry, dispatch],
  );

  return { post, isLoading, isError };
};
